import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Modal, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination, CircularProgress, Alert, Chip } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Header from '../components/Header';
import { getDocumentsVerificationLeads } from '../services/propertyService';
import { API_BASE_URL } from '../config/api';
import { getUserData } from '../services/authService';

const DocumentsVerificationPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    // State for API data and loading
    const [documentsData, setDocumentsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Modal state
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);

    // Check user role on component mount
    useEffect(() => {
        const userData = getUserData();
        setIsAdmin(userData?.Role?.Name === "Admin");
    }, []);

    // Fetch data from API
    useEffect(() => {
        const fetchDocumentsData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const response = await getDocumentsVerificationLeads(page, pageSize);
                
                if (response.success) {
                    setDocumentsData(response.result.data || []);
                    setTotalPages(response.result.totalPages || 1);
                    setTotalRecords(response.result.totalRecords || 0);
                } else {
                    setError(response.message || 'Failed to fetch documents verification data');
                    setDocumentsData([]);
                }
            } catch (err) {
                setError('An error occurred while fetching data');
                console.error('Error fetching documents data:', err);
            } finally {
                setLoading(false);
            }
        };
        
        fetchDocumentsData();
    }, [page, pageSize]);

    // Handle page change
    const handlePageChange = (event, value) => {
        setPage(value);
    };

    // Open view modal
    const openViewModal = (document) => {
        setSelectedDocument(document);
        setCurrentImageIndex(0);
        setIsViewModalOpen(true);
    };

    // Close view modal
    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedDocument(null);
    };
    
    // Process image URL to ensure it's complete
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        return `${API_BASE_URL}/uploads/${imagePath}`;
    };
    
    // Check if file is an image
    const isImage = (path) => {
        if (!path) return false;
        const ext = path.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
    };
    
    // Check if file is a video
    const isVideo = (path) => {
        if (!path) return false;
        const ext = path.split('.').pop().toLowerCase();
        return ['mp4', 'webm', 'ogg', 'mov'].includes(ext);
    };
    
    // Get all media (images and videos) from document data
    const getDocumentMedia = (document) => {
        if (!document) return { images: [], videos: [] };
        
        let mediaArray = [];
        
        if (document.images && Array.isArray(document.images)) {
            mediaArray = document.images;
        } else if (document.images && typeof document.images === 'string') {
            try {
                const parsed = JSON.parse(document.images);
                mediaArray = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                mediaArray = document.images.split(',').map(img => img.trim()).filter(img => img);
            }
        }
        
        const images = mediaArray.filter(item => isImage(item));
        const videos = mediaArray.filter(item => isVideo(item));
        
        return { images, videos };
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'verified':
                return colors.greenAccent[500];
            case 'pending':
                return colors.orangeAccent[500];
            case 'rejected':
                return colors.redAccent[500];
            default:
                return colors.grey[500];
        }
    };
    
    // Handle next image in carousel
    const handleNextImage = (e) => {
        e.stopPropagation();
        if (!selectedDocument) return;
        
        const { images } = getDocumentMedia(selectedDocument);
        if (images.length <= 1) return;
        
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };
    
    // Handle previous image in carousel
    const handlePrevImage = (e) => {
        e.stopPropagation();
        if (!selectedDocument) return;
        
        const { images } = getDocumentMedia(selectedDocument);
        if (images.length <= 1) return;
        
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    // Handle delete document (opens confirmation modal)
    const handleDeleteDocument = (document) => {
        setDocToDelete(document);
        setIsDeleteModalOpen(true);
    };

    // Handle confirmation of delete
    const confirmDelete = async () => {
        if (!docToDelete) return;
        try {
            // Use POST request with JSON body as per the API specification
            const response = await fetch(`${API_BASE_URL}/api/verificationDoc/deleteDoc`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add any other necessary headers like authorization tokens here
                    // 'Authorization': `Bearer ${yourAuthToken}`,
                },
                body: JSON.stringify({
                    Doc_Verification_ID: docToDelete.Doc_Verification_ID
                }),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                // Refresh the data by filtering out the deleted document
                const updatedData = documentsData.filter(doc => doc.Doc_Verification_ID !== docToDelete.Doc_Verification_ID);
                setDocumentsData(updatedData);
                setTotalRecords(prev => prev - 1);
                // Optionally show a success message
            } else {
                // Handle API errors even if response.ok is true but success is false
                setError(result.message || response.statusText || 'Failed to delete document');
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            setError('An error occurred while deleting the document');
        } finally {
            setIsDeleteModalOpen(false);
            setDocToDelete(null);
        }
    };

    // Handle download reports
    const handleDownloadReports = () => {
        // Implement report download functionality
        console.log('Downloading documents verification reports...');
    };

    return (
        <Box m="20px">
            <Header title="DOCUMENTS VERIFICATION" subtitle={`Manage Property Documents Verification (${totalRecords} total)`} />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#FFFFFF' }}>
                    <Table>
                        <TableHead>
                                <TableRow>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Property Type</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Address</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>City</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Name</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Created Date</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documentsData.map((document) => (
                                    <TableRow key={document.Doc_Verification_ID}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{document.Property_Type}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{document.District_Tehsil}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{document.City}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{document.Name}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{new Date(document.IsCreated).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                        <Box display="flex" gap={1}>
                                            <IconButton 
                                                onClick={() => openViewModal(document)}
                                                sx={{ 
                                                    color: '#000000',
                                                    '&:hover': {
                                                        backgroundColor: '#F5F5F5'
                                                    }
                                                }}
                                            >
                                                <VisibilityOutlinedIcon />
                                            </IconButton>
                                            {isAdmin && (
                                                <IconButton 
                                                    onClick={() => handleDeleteDocument(document)}
                                                    sx={{ 
                                                        color: '#D32F2F',
                                                        '&:hover': {
                                                            backgroundColor: '#FFEBEE'
                                                        }
                                                    }}
                                                >
                                                    <DeleteOutlineOutlinedIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
            )}

            {!loading && (
                <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination
                        count={Math.ceil(totalRecords / pageSize)} 
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: '#000000',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: '#F5F5F5'
                                }
                            },
                            '& .MuiPaginationItem-root.Mui-selected': {
                                backgroundColor: '#D32F2F',
                                color: '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#B71C1C'
                                }
                            },
                            '& .MuiPaginationItem-root.Mui-disabled': {
                                color: '#9E9E9E'
                            }
                        }}
                            />
                        </Box>
            )}

            {/* View Document Modal */}
            <Modal open={isViewModalOpen} onClose={closeViewModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: '800px',
                    bgcolor: '#FFFFFF',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    {selectedDocument && (
                        <>
                            <Typography variant="h6" component="h2" mb={2} color="#000000">
                                Document Details
                            </Typography>

                            {getDocumentMedia(selectedDocument).images.length > 0 && (
                                <Box position="relative" mb={3}>
                                    <img
                                        src={getImageUrl(getDocumentMedia(selectedDocument).images[currentImageIndex])}
                                        alt={`Document ${currentImageIndex + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '300px',
                                            objectFit: 'cover',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    {getDocumentMedia(selectedDocument).images.length > 1 && (
                                        <>
                                            <IconButton
                                                onClick={handlePrevImage}
                                                sx={{
                                                    position: 'absolute',
                                                    left: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#FFFFFF',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,0,0,0.7)'
                                                    }
                                                }}
                                            >
                                                <NavigateBeforeIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={handleNextImage}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#FFFFFF',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,0,0,0.7)'
                                                    }
                                                }}
                                            >
                                                <NavigateNextIcon />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            )}

                            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Document ID</Typography>
                                    <Typography color="#000000">{selectedDocument.Doc_Verification_ID}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Name</Typography>
                                    <Typography color="#000000">{selectedDocument.Name}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Property Type</Typography>
                                    <Typography color="#000000">{selectedDocument.Property_Type}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Address</Typography>
                                    <Typography color="#000000">{selectedDocument.District_Tehsil}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">City</Typography>
                                    <Typography color="#000000">{selectedDocument.City}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Contact Number</Typography>
                                    <Typography color="#000000">{selectedDocument.Contact_Number}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Created Date</Typography>
                                    <Typography color="#000000">{new Date(selectedDocument.IsCreated).toLocaleDateString()}</Typography>
                                </Box>
                            </Box>

                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <Button 
                                    variant="contained" 
                                    onClick={closeViewModal}
                                    sx={{
                                        backgroundColor: '#D32F2F',
                                        '&:hover': {
                                            backgroundColor: '#B71C1C'
                                        }
                                    }}
                                >
                                    Close
                                </Button>
                        </Box>
                        </>
                    )}
                </Box>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                aria-labelledby="delete-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: colors.primary[400],
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px'
                }}>
                    <Typography variant="h6" component="h2" mb={2}>
                        Confirm Delete
                    </Typography>
                    <Typography mb={3}>
                        Are you sure you want to delete this document verification record? This action cannot be undone.
                    </Typography>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            onClick={() => setIsDeleteModalOpen(false)}
                            variant="outlined"
                            sx={{
                                color: colors.grey[100],
                                borderColor: colors.grey[100],
                                '&:hover': {
                                    borderColor: colors.grey[100],
                                    backgroundColor: colors.grey[700]
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            variant="contained"
                            sx={{
                                backgroundColor: colors.redAccent[500],
                                '&:hover': {
                                    backgroundColor: colors.redAccent[700]
                                }
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default DocumentsVerificationPage; 